/**
 * Mermaid `classDiagram` → DiagramData importer (pure, unit-tested).
 *
 * Supports a practical subset:
 *   Header:    `classDiagram`
 *   Classes:   `class Animal { ... }` blocks, or bare `class Animal`
 *   Members:   inside a block, or `Animal : +int age` one-liners
 *              (visibility +,-,#,~; "type name", "name : type", or `method()`)
 *   Relations: `<|--` inherit, `*--` compose, `o--` aggregate,
 *              `-->` associate, `..>` depend, `..|>` realize, `--` link
 *
 * Relationships become ordinary DiagramEdges whose arrow markers
 * (data.arrowStart/arrowEnd) encode the UML relation — reusing the shipped
 * edge-marker set (triangle-hollow, diamond, diamond-filled, …). Class boxes
 * carry `classContent`, so they round-trip through the same structured-node
 * path as tables/resources/endpoints.
 */

import type { DiagramData, DiagramNode, DiagramEdge, UMLClassContent } from '../types/schema'

export interface MermaidClassImportResult {
  data: DiagramData
  warnings: string[]
}

type Member = NonNullable<UMLClassContent['attributes']>[number] & { signature?: string; isAbstract?: boolean }

interface ParsedClass {
  name: string
  stereotype?: string
  generics?: string
  attributes: NonNullable<UMLClassContent['attributes']>
  methods: NonNullable<UMLClassContent['methods']>
}

const VIS = new Set(['+', '-', '#', '~'])

/** Parse one member declaration into either an attribute or a method record. */
function parseMember(raw: string): { kind: 'attr' | 'method'; member: Member } | null {
  let text = raw.trim()
  if (!text) return null
  let visibility: '+' | '-' | '#' | '~' | undefined
  if (VIS.has(text[0])) {
    visibility = text[0] as any
    text = text.slice(1).trim()
  }
  // Strip Mermaid classifier suffixes ($ static, * abstract).
  let isStatic = false
  let isAbstract = false
  if (text.endsWith('$')) { isStatic = true; text = text.slice(0, -1).trim() }
  if (text.endsWith('*')) { isAbstract = true; text = text.slice(0, -1).trim() }

  if (text.includes('(')) {
    // Method: keep "name(args) returnType" as the signature, normalising the
    // trailing return type to ": type" when present.
    const m = /^([^()]*\([^)]*\))\s*(.*)$/.exec(text)
    const sig = m ? (m[2] ? `${m[1]}: ${m[2]}` : m[1]) : text
    return { kind: 'method', member: { name: sig, visibility, isStatic, isAbstract, signature: sig } }
  }

  // Attribute. Mermaid uses "type name"; also accept "name : type".
  let name = text
  let type: string | undefined
  if (text.includes(':')) {
    const [n, t] = text.split(':')
    name = n.trim(); type = t.trim()
  } else {
    const parts = text.split(/\s+/)
    if (parts.length === 2) { type = parts[0]; name = parts[1] }
  }
  return { kind: 'attr', member: { name, type, visibility, isStatic } }
}

function ensureClass(name: string, classes: Map<string, ParsedClass>): ParsedClass {
  let c = classes.get(name)
  if (!c) {
    c = { name, attributes: [], methods: [] }
    classes.set(name, c)
  }
  return c
}

function addMember(cls: ParsedClass, raw: string) {
  const parsed = parseMember(raw)
  if (!parsed) return
  if (parsed.kind === 'method') {
    const { signature, name, ...rest } = parsed.member
    cls.methods.push({ signature: signature ?? name, visibility: rest.visibility, isStatic: rest.isStatic, isAbstract: rest.isAbstract })
  } else {
    const { signature, ...attr } = parsed.member
    cls.attributes.push(attr)
  }
}

/** Relation connector → edge marker config. LEFT/RIGHT refer to the two operands. */
interface RelSpec {
  re: RegExp
  /** Which operand becomes the edge source. */
  build: (left: string, right: string) => Pick<DiagramEdge, 'source' | 'target'> & {
    arrowEnd?: string; arrowStart?: string; dashed?: boolean; edgeType?: DiagramEdge['edgeType']
  }
}

// Order matters: match longer / dashed connectors before their substrings.
const RELATIONS: RelSpec[] = [
  { re: /\.\.\|>/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'triangle-hollow', dashed: true, edgeType: 'relationship' }) }, // realization
  { re: /<\|\.\./, build: (l, r) => ({ source: r, target: l, arrowEnd: 'triangle-hollow', dashed: true, edgeType: 'relationship' }) },
  { re: /<\|--/, build: (l, r) => ({ source: r, target: l, arrowEnd: 'triangle-hollow', edgeType: 'relationship' }) }, // inheritance (parent left)
  { re: /--\|>/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'triangle-hollow', edgeType: 'relationship' }) }, // inheritance (parent right)
  { re: /\*--/, build: (l, r) => ({ source: r, target: l, arrowEnd: 'diamond-filled', edgeType: 'relationship' }) },     // composition
  { re: /--\*/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'diamond-filled', edgeType: 'relationship' }) },
  { re: /o--/, build: (l, r) => ({ source: r, target: l, arrowEnd: 'diamond', edgeType: 'relationship' }) },             // aggregation
  { re: /--o/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'diamond', edgeType: 'relationship' }) },
  { re: /\.\.>/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'open-arrow', dashed: true, edgeType: 'dependency' }) }, // dependency
  { re: /<\.\./, build: (l, r) => ({ source: r, target: l, arrowEnd: 'open-arrow', dashed: true, edgeType: 'dependency' }) },
  { re: /-->/, build: (l, r) => ({ source: l, target: r, arrowEnd: 'arrow', edgeType: 'association' }) },                 // association
  { re: /<--/, build: (l, r) => ({ source: r, target: l, arrowEnd: 'arrow', edgeType: 'association' }) },
  { re: /\.\./, build: (l, r) => ({ source: l, target: r, dashed: true, edgeType: 'dependency' }) },
  { re: /--/, build: (l, r) => ({ source: l, target: r, edgeType: 'link' }) },                                            // plain link
]

/** Split `ClassA "1" <|-- "*" ClassB : label` into operands, relation token, label. */
function parseRelation(line: string): { left: string; right: string; spec: RelSpec; label?: string } | null {
  let work = line
  let label: string | undefined
  const colon = work.indexOf(':')
  if (colon >= 0) { label = work.slice(colon + 1).trim(); work = work.slice(0, colon) }
  for (const spec of RELATIONS) {
    const m = spec.re.exec(work)
    if (!m) continue
    const left = work.slice(0, m.index).trim().replace(/"[^"]*"/g, '').trim()
    const right = work.slice(m.index + m[0].length).trim().replace(/"[^"]*"/g, '').trim()
    if (!left || !right) return null
    // Strip cardinality and generics noise from bare identifiers.
    const clean = (s: string) => s.split(/\s+/)[0].replace(/~.*~/, '')
    return { left: clean(left), right: clean(right), spec, label }
  }
  return null
}

/**
 * Parse Mermaid `classDiagram` text into DiagramData. Always returns a valid
 * DiagramData (possibly empty); non-fatal issues collect as warnings.
 */
export function parseMermaidClassDiagram(input: string): MermaidClassImportResult {
  const warnings: string[] = []
  const classes = new Map<string, ParsedClass>()
  const edges: DiagramEdge[] = []

  const lines = input.split('\n').map(l => l.trim()).filter(Boolean)
  let i = 0
  let sawHeader = false
  while (i < lines.length) {
    const line = lines[i]; i++
    if (line.startsWith('%%')) continue
    if (/^classDiagram(-v2)?\b/.test(line)) { sawHeader = true; continue }
    if (/^(direction|title)\b/.test(line)) continue

    // `class Name { ... }` or `class Name`
    const classMatch = /^class\s+([A-Za-z_][\w]*)(~[^~]+~)?\s*(\{)?\s*$/.exec(line)
    if (classMatch) {
      const cls = ensureClass(classMatch[1], classes)
      // Mermaid generics use `~T~`; normalise to UML-style `<T>`.
      if (classMatch[2]) cls.generics = '<' + classMatch[2].replace(/~/g, '') + '>'
      if (classMatch[3] === '{') {
        // Consume the block body until the closing brace.
        while (i < lines.length && lines[i] !== '}') {
          const body = lines[i]; i++
          if (body.startsWith('<<') && body.endsWith('>>')) { cls.stereotype = body.slice(2, -2).trim(); continue }
          addMember(cls, body)
        }
        i++ // skip '}'
      }
      continue
    }

    // `<<interface>> Name` stereotype line
    const stereoMatch = /^<<([^>]+)>>\s+([A-Za-z_][\w]*)/.exec(line)
    if (stereoMatch) { ensureClass(stereoMatch[2], classes).stereotype = stereoMatch[1].trim(); continue }

    // `ClassName : member` one-liner (and not a relation)
    const oneLiner = /^([A-Za-z_][\w]*)\s*:\s*(.+)$/.exec(line)
    if (oneLiner && !RELATIONS.some(r => r.re.test(line.split(':')[0]))) {
      // Could be a stereotype assignment `Name : <<service>>`.
      const rhs = oneLiner[2].trim()
      if (rhs.startsWith('<<') && rhs.endsWith('>>')) ensureClass(oneLiner[1], classes).stereotype = rhs.slice(2, -2).trim()
      else addMember(ensureClass(oneLiner[1], classes), rhs)
      continue
    }

    // Relation line
    const rel = parseRelation(line)
    if (rel) {
      ensureClass(rel.left, classes); ensureClass(rel.right, classes)
      const b = rel.spec.build(rel.left, rel.right)
      edges.push({
        id: `e-${b.source}-${b.target}-${edges.length}`,
        source: b.source,
        target: b.target,
        label: rel.label,
        edgeType: b.edgeType,
        type: 'custom',
        data: {
          ...(b.arrowEnd ? { arrowEnd: b.arrowEnd } : {}),
          ...(b.arrowStart ? { arrowStart: b.arrowStart } : {}),
          ...(b.dashed ? { lineStyle: 'dashed' as const } : {}),
        },
      })
      continue
    }

    warnings.push(`Skipped unrecognised line: ${line}`)
  }

  if (!sawHeader) warnings.push('No `classDiagram` header found — parsed best-effort.')

  // Lay classes out on a simple grid; auto-layout can refine afterwards.
  const nodes: DiagramNode[] = []
  let idx = 0
  const COLS = 3
  for (const cls of classes.values()) {
    nodes.push({
      id: cls.name,
      label: cls.name,
      nodeType: 'component',
      position: { x: (idx % COLS) * 300, y: Math.floor(idx / COLS) * 260 },
      classContent: {
        className: cls.name,
        stereotype: cls.stereotype,
        generics: cls.generics,
        attributes: cls.attributes,
        methods: cls.methods,
      },
    })
    idx++
  }

  const data: DiagramData = {
    diagramType: 'class',
    title: 'Class Diagram',
    nodes,
    edges,
    layout: { algorithm: 'dagre', direction: 'TB' },
  }
  return { data, warnings }
}
